import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminNormative,
  UpdateNormativeNewImageBody,
  UpdateNormativeRemoveImageBody,
} from "../../../../../http/vertigrip/types/normatives";
import { useNormativesApi } from "../../../../../http/vertigrip/useNormatives";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Textarea from "../../../../ui/Textarea";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateNormativeForm {
  name: string | null;
  order: number | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  visibility: VisibilityScopeValue;
}

interface NormativeDetailsProps {
  normative?: AdminNormative;
  disabled?: boolean;
}

const NormativeDetails: FC<NormativeDetailsProps> = ({ normative, disabled }: NormativeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normativesApi = useNormativesApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: normative?.image,
    isLoading: !normative,
    disabled,
  });

  const defaultValues: UpdateNormativeForm = {
    name: normative?.name ?? null,
    description: normative?.description ?? null,
    descriptionIt: normative?.descriptionIt ?? null,
    descriptionDe: normative?.descriptionDe ?? null,
    descriptionEs: normative?.descriptionEs ?? null,
    descriptionFr: normative?.descriptionFr ?? null,
    order: normative?.order ?? 50,
    visibility: normative?.visibility ?? "hidden",
  };

  const submit = async (body: UpdateNormativeNewImageBody | UpdateNormativeRemoveImageBody) => {
    try {
      await normativesApi.updateAdminNormative(normative?.id!, body);
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => async (formData: UpdateNormativeForm) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      order: formData.order!,
      description: formData.description!,
      descriptionIt: formData.descriptionIt?.trim() ?? null,
      descriptionDe: formData.descriptionDe?.trim() ?? null,
      descriptionEs: formData.descriptionEs?.trim() ?? null,
      descriptionFr: formData.descriptionFr?.trim() ?? null,
      visibility: formData.visibility,
    };

    if (newImage) {
      try {
        const signedUrl = await normativesApi.generateUploadImageUrl(normative?.id!, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateNormativeNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateNormativeRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateNormativeForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [normative]);

        useEffect(() => {
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="description"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.description}
                    dialogType="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("description", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NormativeDetails;
